import {
  ButtonDark,
  CaseCard,
  FeaturedCase,
  IMG,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  FinalCta,
} from "../lib/shared"
import { useCollection } from "../lib/content"
import { Seo, breadcrumbSchema, itemListSchema } from "../lib/seo"

const ANATOMY = [
  { n: "01", t: "The challenge", d: "What wasn't working, and what it was costing the business." },
  { n: "02", t: "The thinking", d: "The key decision we made, and why we made it." },
  { n: "03", t: "The build", d: "What we designed and built, and how it fits together." },
  { n: "04", t: "The outcome", d: "What changed for the business once it was live." },
]

export default function Work() {
  const { items } = useCollection("caseStudies")
  const works = items.filter((w) => w.status === "Published")
  const [featured, ...rest] = works

  return (
    <>
      <Seo
        title="Selected work and case studies"
        description="Websites, platforms and brands Sableboxx has built, with the thinking behind each one and what changed for the business afterwards."
        jsonLd={[
          itemListSchema(
            works.map((w) => ({ name: w.title, path: `/work/${w.slug}` })),
            "Sableboxx case studies",
          ),
          breadcrumbSchema([{ name: "Work", path: "/work" }]),
        ]}
      />
      <PageHero
        eyebrow="Our work"
        title="Built for businesses with something to say"
        intro="Websites, platforms and brands we've built for businesses in Nigeria and beyond, with the thinking behind each one and what changed afterwards."
        image={IMG.workHero}
      />

      <Section tone="paper">
        {featured ? (
          <>
            <Reveal>
              <FeaturedCase work={featured} />
            </Reveal>
            {rest.length > 0 && (
              <div className="mt-20 grid grid-cols-1 gap-6 border-t border-neutral-200 pt-20 sm:grid-cols-2 lg:mt-24 lg:pt-24">
                {rest.map((w, i) => (
                  <Reveal key={w.id} delay={(i % 2) * 90}>
                    <CaseCard work={w} />
                  </Reveal>
                ))}
              </div>
            )}
          </>
        ) : (
          <Reveal>
            <div className="rounded-2xl border border-neutral-200 bg-mist px-8 py-16 lg:text-center">
              <h2 className="text-[24px] font-bold tracking-[-0.02em]">
                Case studies are being written up
              </h2>
              <p className="mt-3 max-w-md lg:mx-auto text-[16px] leading-[1.7] text-neutral-600">
                In the meantime, we&rsquo;re happy to walk you through recent
                projects on a call.
              </p>
              <div className="mt-8 flex lg:justify-center">
                <ButtonDark to="/contact">Get in touch</ButtonDark>
              </div>
            </div>
          </Reveal>
        )}
      </Section>

      {/* How each case study is written */}
      <Section tone="mist">
        <SectionHeader
          eyebrow="How we write them up"
          title="Proof, not just pretty pictures"
          intro="Every case study answers the same four questions, so you can judge the thinking as well as the result."
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ANATOMY.map((c, i) => (
            <Reveal key={c.n} delay={i * 80}>
              <div className="h-full rounded-2xl border border-neutral-200 bg-white p-7">
                <span className="text-[13px] font-black tracking-widest text-accent">
                  {c.n}
                </span>
                <h3 className="mt-4 text-[19px] font-bold tracking-[-0.02em]">
                  {c.t}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.7] text-neutral-600">
                  {c.d}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <FinalCta />
    </>
  )
}
